import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, DollarSign, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { useCommissionReport, type ConsultantCommissionRow, type PaymentDetail } from '@/hooks/useCommissionReport';
import { format, parseISO } from 'date-fns';
const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function formatPct(value: number) {
  return `${value.toFixed(1)}%`;
}

export default function CommissionReport() {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const { data, paymentDetails, isLoading, error, plan } = useCommissionReport(year, month);
  const totals = data.reduce(
    (acc, r) => ({
      goal: acc.goal + r.goalAmount,
      sales: acc.sales + r.totalSales,
      cashInNew: acc.cashInNew + r.cashInNew,
      cashInRecurring: acc.cashInRecurring + r.cashInRecurring,
      cashInTotal: acc.cashInTotal + r.totalCashIn,
      commission: acc.commission + r.result.commissionAmount,
    }),
    { goal: 0, sales: 0, cashInNew: 0, cashInRecurring: 0, cashInTotal: 0, commission: 0 }
  );

  const handleExportCSV = () => {
    if (!data.length) return;
    // Summary section
    const headers = ['Consultor', 'Meta', 'Vendas', '% Atingimento', 'Tier', 'Entradas', 'Recorrência', 'Total Cash-In', '% Comissão', 'Valor Comissão'];
    const rows = data.map(r => [
      r.consultantName,
      r.goalAmount,
      r.totalSales,
      r.result.achievementPct.toFixed(1),
      r.result.tier ? `${r.result.tier.min_goal_pct}–${r.result.tier.max_goal_pct ?? '∞'}%` : '-',
      r.cashInNew,
      r.cashInRecurring,
      r.totalCashIn,
      r.result.totalCommissionPct.toFixed(1),
      r.result.commissionAmount.toFixed(2),
    ]);
    rows.push(['TOTAL', totals.goal, totals.sales, '', '', totals.cashInNew, totals.cashInRecurring, totals.cashInTotal, '', totals.commission.toFixed(2)]);

    const summaryCSV = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');

    // Detail section
    const nameMap = new Map(data.map(r => [r.consultantId, r.consultantName]));
    const detailHeaders = ['Consultor', 'Cliente', 'Data Pagamento', 'Método', 'Valor', 'Tipo'];
    const detailRows = paymentDetails
      .sort((a, b) => a.consultantId.localeCompare(b.consultantId))
      .map(p => [
        nameMap.get(p.consultantId) || 'Desconhecido',
        `"${p.clientName}"`,
        p.paidAt ? format(parseISO(p.paidAt), 'dd/MM/yyyy') : '-',
        p.paymentType,
        p.amount.toFixed(2),
        p.isRecurring ? 'Recorrência' : 'Novo',
      ]);

    const detailCSV = [detailHeaders.join(','), ...detailRows.map(r => r.join(','))].join('\n');

    const csv = summaryCSV + '\n\n\n' + 'DETALHAMENTO DE PAGAMENTOS\n' + detailCSV;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comissoes-us-${year}-${String(month).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/reports')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <DollarSign className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Relatório de Comissões</h1>
            <p className="text-muted-foreground">Comissões dos consultores EUA por período</p>
          </div>
        </div>
        <Button onClick={handleExportCSV} disabled={!data.length}>
          <Download className="h-4 w-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-end gap-4 flex-wrap">
        <div className="w-[160px]">
          <label className="text-sm font-medium mb-2 block">Mês</label>
          <Select value={String(month)} onValueChange={v => setMonth(Number(v))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((m, i) => (
                <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[120px]">
          <label className="text-sm font-medium mb-2 block">Ano</label>
          <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {plan && (
          <Badge variant="secondary" className="h-9 px-3 text-sm">
            Plano: {plan.name}
          </Badge>
        )}
        {!plan && !isLoading && (
          <Badge variant="destructive" className="h-9 px-3 text-sm">
            Nenhum plano vigente
          </Badge>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Comissões — {MONTHS[month - 1]} {year}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="p-6 text-destructive">Erro ao carregar dados: {(error as Error).message}</div>
          ) : !data.length ? (
            <div className="p-6 text-muted-foreground text-center">Nenhum consultor com meta cadastrada para este período.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Consultor</TableHead>
                    <TableHead className="text-right">Meta</TableHead>
                    <TableHead className="text-right">Vendas</TableHead>
                    <TableHead className="text-right">% Ating.</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead className="text-right">Entradas</TableHead>
                    <TableHead className="text-right">Recorrência</TableHead>
                    <TableHead className="text-right">Total Cash-In</TableHead>
                    <TableHead className="text-right">% Comissão</TableHead>
                    <TableHead className="text-right">Valor Comissão</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((row) => (
                    <TableRow key={row.consultantId}>
                      <TableCell className="font-medium">{row.consultantName}</TableCell>
                      <TableCell className="text-right">{formatCurrency(row.goalAmount)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(row.totalSales)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={row.result.achievementPct >= 100 ? 'default' : 'secondary'} className="font-mono">
                          {formatPct(row.result.achievementPct)}
                        </Badge>
                      </TableCell>
                      <TableCell>{row.result.tier ? `${row.result.tier.min_goal_pct}–${row.result.tier.max_goal_pct ?? '∞'}%` : '-'}</TableCell>
                      <TableCell className="text-right">{formatCurrency(row.cashInNew)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(row.cashInRecurring)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(row.totalCashIn)}</TableCell>
                      <TableCell className="text-right font-mono">
                        <span>{formatPct(row.result.totalCommissionPct)}</span>
                        {row.result.isRampUp && (
                          <Badge variant="outline" className="ml-1.5 text-xs border-primary/50 text-primary">
                            Ramp-up +{formatPct(row.result.rampupBonusPct)}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-bold text-primary">{formatCurrency(row.result.commissionAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow className="font-bold">
                    <TableCell>TOTAL</TableCell>
                    <TableCell className="text-right">{formatCurrency(totals.goal)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(totals.sales)}</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell className="text-right">{formatCurrency(totals.cashInNew)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(totals.cashInRecurring)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(totals.cashInTotal)}</TableCell>
                    <TableCell />
                    <TableCell className="text-right text-primary">{formatCurrency(totals.commission)}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
